import {Pinia} from 'pinia'
import {useWorkspaceStore} from '../../../stores/WorkspaceStore/useWorkspaceStore'
import {RouteLocationNormalized, Router} from 'vue-router'
import {KnownError} from '@toolify/server/src/middleware/ApiResponseMiddleware/types/KnownError'
import {ApiException} from '@toolify/server/src/middleware/ApiResponseMiddleware/errors/ApiException'
import {useMessages} from '@toolify/client/src/modules/notifications/composables/useMessages'
import {useCacheStore} from '@toolify/client/src/stores/CacheStore/useCacheStore'
import {LocalPreferenceType} from '@toolify/client/src/stores/LocalPreferenceStore/enum/LocalPreferenceType'
import {useLocalPreferenceStore} from '@toolify/client/src/stores/LocalPreferenceStore/useLocalPreferenceStore'
import {isStackableOverlayNavigation} from './isStackableOverlayNavigation'
import {getUnavailableWorkspaceRedirect} from './getUnavailableWorkspaceRedirect'
import {PermissionType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/PermissionType'
import {HTTPStatusCode} from '@toolify/server/src/middleware/ApiResponseMiddleware/types/HttpStatusCode'
import {i18n} from '@toolify/client/src/setup/i18n'
import {getWorkspaceAccessByName} from '@toolify/client/src/modules/workspace/functions/getWorkspaceAccessByName'
import {useViewAsStore} from '@toolify/client/src/stores/ViewAsStore/useViewAsStore'
import {readViewAsStateFromStorage} from '@toolify/client/src/stores/ViewAsStore/functions/readViewAsStateFromStorage'

export function ensureWorkspaceRouterHookFactory(pinia: Pinia, router: Router) {
  return async function ensureWorkspaceRouterHook(to: RouteLocationNormalized, from: RouteLocationNormalized) {
    // currentWorkspace belongs to the page under the overlays, sidebar included. A layer opened from another
    // workspace (an item card from its notification or link) gets that workspace from useLayerContext instead.
    if(isStackableOverlayNavigation(to, from)) {
      return
    }

    const workspaceName = to.params.workspaceName
    if(workspaceName) {
      const cacheStore = useCacheStore(pinia)
      const workspaceStore = useWorkspaceStore(pinia)
      const messages = useMessages()
      const localPreferenceStore = useLocalPreferenceStore(pinia)
      const viewAsState = readViewAsStateFromStorage()
      const isCurrentWorkspaceStillAuthorized = workspaceName === workspaceStore.currentWorkspace?.name
        && workspaceStore.hasPermission(PermissionType.readWorkspace)
      if(isCurrentWorkspaceStillAuthorized) {
        void localPreferenceStore.set(LocalPreferenceType.LastWorkspaceName, workspaceName as string)
        return
      }
      try {
        const {workspace, permissions} = await getWorkspaceAccessByName(workspaceName as string, pinia)
        if(viewAsState.token && viewAsState.workspaceId !== workspace.id) {
          await useViewAsStore(pinia).stop(router.resolve(to.fullPath).href)
          return
        }
        const canReadWorkspace = permissions.some(permission => {
          return permission.type === PermissionType.readWorkspace && permission.value === true
        })
        if(!canReadWorkspace) {
          throw new ApiException(KnownError.NoPermissions, 'No sufficient permissions', HTTPStatusCode.Forbidden)
        }
        if(workspace.name !== workspaceName) {
          // return to the same url with the correct workspace name
          return {
            ...to,
            params: {
              ...to.params,
              workspaceName: workspace.name,
            },
          }
        }
        workspaceStore.currentWorkspace = workspace
        workspaceStore.setCurrentWorkspacePermissionMap(permissions)
        void localPreferenceStore.set(LocalPreferenceType.LastWorkspaceName, workspace.name)
      } catch(e) {
        if(e instanceof ApiException && viewAsState.token) {
          await useViewAsStore(pinia).stop(router.resolve(to.fullPath).href)
          return
        }
        if(e instanceof ApiException) {
          cacheStore.setCacheItem(`workspace-by-name-${workspaceName}`, null)
          workspaceStore.currentWorkspace = null
          messages.warning(e.name === KnownError.NotFound
            ? i18n.global.t('workspaces.workspaceNotFound')
            : i18n.global.t('workspaces.noAccessToWorkspace'))
          return getUnavailableWorkspaceRedirect(workspaceName as string)
        }
        throw e
      }
    }
  }
}
